import { cn, domPassthrough } from './chunk-64WACXM7.js';
import { formatValue } from './chunk-6ZCU3BQN.js';
import React from 'react';
import { useFormatContext } from '@almadar/ui/hooks';

// lib/inlineText.ts
var INLINE_TEXT_ATTR = "data-inline-text";
var variantStyles = {
  h1: "text-4xl heading-voice text-foreground",
  h2: "text-3xl heading-voice text-foreground",
  h3: "text-2xl heading-voice text-foreground",
  h4: "text-xl heading-voice text-foreground",
  h5: "text-lg heading-voice text-foreground",
  h6: "text-base heading-voice text-foreground",
  heading: "text-2xl heading-voice text-foreground",
  subheading: "text-lg heading-voice text-foreground",
  body1: "text-base font-normal text-foreground",
  body2: "text-sm font-normal text-foreground",
  body: "text-base font-normal text-foreground",
  caption: "text-xs font-normal text-muted-foreground",
  overline: "text-xs uppercase tracking-wide font-bold text-muted-foreground",
  small: "text-sm font-normal text-foreground",
  large: "text-lg font-medium text-foreground",
  label: "text-sm font-medium text-foreground"
};
var colorStyles = {
  primary: "text-foreground",
  secondary: "text-muted-foreground",
  muted: "text-muted-foreground",
  error: "text-error",
  success: "text-success",
  warning: "text-warning",
  inherit: "text-inherit"
};
var weightStyles = {
  // Neutral: an atom default of "none" must not override the variant's baked
  // weight (C-PATTERN-ENUM-BLOCKS-NEUTRAL-OVERRIDE — `variant={h2}` rendered
  // tiny because the ui-typography atom's `weight: light`/`size: xs` defaults
  // clobbered every variant).
  none: "",
  light: "font-light",
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold"
};
var defaultElements = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  heading: "h2",
  subheading: "h3",
  body1: "p",
  body2: "p",
  body: "p",
  caption: "span",
  overline: "span",
  small: "span",
  large: "p",
  label: "span"
};
var typographySizeStyles = {
  none: "",
  xs: "text-xs",
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
  "3xl": "text-3xl"
};
var overflowStyles = {
  visible: "overflow-visible",
  hidden: "overflow-hidden",
  wrap: "break-words overflow-hidden",
  "clamp-2": "overflow-hidden line-clamp-2",
  "clamp-3": "overflow-hidden line-clamp-3"
};
var Typography = ({
  variant: variantProp,
  level,
  color = "primary",
  align,
  weight,
  size,
  truncate = false,
  overflow,
  as,
  id,
  className,
  style,
  format,
  content,
  children,
  ...rest
}) => {
  const fmt = useFormatContext();
  const variant = variantProp ?? (level ? `h${level}` : "body1");
  const Component = as || defaultElements[variant];
  let body = children ?? content;
  if (format !== void 0 && format !== "none" && (typeof body === "string" || typeof body === "number" || body instanceof Date)) {
    body = formatValue(body, format, fmt);
  }
  return React.createElement(
    Component,
    {
      ...domPassthrough(rest),
      id,
      className: cn(
        variantStyles[variant],
        colorStyles[color],
        weight && weightStyles[weight],
        size && typographySizeStyles[size],
        align && `text-${align}`,
        // `min-w-0` rides along with truncate/wrap/clamp: a flex or grid
        // item's default `min-width: auto` refuses to shrink below its
        // content's intrinsic width, so ellipsis/wrap silently does nothing
        // in the single most common placement (a row next to a fixed-width
        // control) unless the item can also shrink to zero. (Spelled out as
        // just "truncate", not "truncate overflow-hidden text-ellipsis" —
        // tailwind-merge treats those as the same conflict group and drops
        // "truncate" as the earlier-declared class, silently losing its
        // `white-space: nowrap`.)
        truncate && "truncate min-w-0",
        overflow && cn(overflowStyles[overflow], overflow !== "visible" && "min-w-0"),
        className
      ),
      style,
      // Text that comes from the `content` prop can be edited in place.
      ...children == null && typeof content === "string" ? { [INLINE_TEXT_ATTR]: "content" } : {}
    },
    body
  );
};
Typography.displayName = "Typography";

export { INLINE_TEXT_ATTR, Typography };
